import React, { FC, ReactNode } from 'react';
import { PlanOverviewV3Response, PlanUsageLevel } from '@dreamai/types';
import { formatPlanPrice } from '../../utils/planOverview';
import DreamMarque from '../common/DreamMarque';
import { ReactComponent as DreamMarqueOutline } from '../../icons/brand/dream-marque-outline.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import './personal-plan-billing.scss';

interface PersonalPlanBillingProps {
  overview: PlanOverviewV3Response;
  onStartSubscription: () => void;
  startingSubscription: boolean;
  onCancelSubscription: () => void;
  onRestoreSubscription: () => void;
  restoringSubscription: boolean;
  error: string | null;
}

export function formatPlanDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function daysLeftLabel(
  isoString: string,
  now = new Date(),
): string | null {
  const days = Math.ceil(
    (new Date(isoString).getTime() - now.getTime()) / 86_400_000,
  );
  if (days <= 0) return null;
  return `${days} ${days === 1 ? 'day' : 'days'} left`;
}

const USAGE_LEVEL_LABELS: Record<PlanUsageLevel, string> = {
  unlimited: 'Unlimited',
  ok: "You're fine",
  low: 'Running low',
  exhausted: 'Used up',
};

interface PanelProps {
  eyebrow: ReactNode;
  headline: ReactNode;
  caption?: ReactNode;
  meta?: ReactNode;
  testId?: string;
}

/** Sand panel with the Dream marque: eyebrow, headline, caption and a right-hand meta line. */
const Panel: FC<PanelProps> = ({
  eyebrow,
  headline,
  caption,
  meta,
  testId,
}) => (
  <div className='plan-billing-panel' data-testid={testId}>
    <div className='plan-billing-bubble' aria-hidden='true'>
      <DreamMarque />
    </div>
    <div className='plan-billing-panel-body'>
      <p className='plan-billing-eyebrow'>{eyebrow}</p>
      <p className='plan-billing-headline'>{headline}</p>
      {caption && <p className='plan-billing-caption'>{caption}</p>}
    </div>
    {meta && <p className='plan-billing-meta'>{meta}</p>}
  </div>
);

/**
 * Personal Plan & billing under billing v3: one card, one primary action, and
 * never a credit number. Mirrors the organisation Plan & billing card.
 */
const PersonalPlanBilling: FC<PersonalPlanBillingProps> = ({
  overview,
  onStartSubscription,
  startingSubscription,
  onCancelSubscription,
  onRestoreSubscription,
  restoringSubscription,
  error,
}) => {
  const { source, usage, subscription, offer } = overview;
  const offerPrice = offer ? formatPlanPrice(offer) : null;
  const canStart = overview.canStartSubscription && offer !== null;

  const startCta = (line: string, label: string) =>
    canStart ? (
      <div className='plan-billing-cta'>
        <p className='plan-billing-cta-line'>{line}</p>
        <button
          type='button'
          className='btn-plan-primary plan-billing-button'
          onClick={onStartSubscription}
          disabled={startingSubscription}
          data-testid='plan-billing-start'
        >
          {startingSubscription ? 'Opening…' : label}
          <ArrowRightIcon
            className='plan-billing-button-icon'
            aria-hidden='true'
          />
        </button>
      </div>
    ) : null;

  let body: ReactNode;
  let footer: ReactNode = null;

  switch (source) {
    case 'free_period': {
      const endsAt = overview.freePeriodEndsAt;
      const daysLeft = endsAt ? daysLeftLabel(endsAt) : null;
      body = (
        <div className='plan-billing-hero'>
          <DreamMarqueOutline
            className='plan-billing-hero-marque'
            aria-hidden='true'
          />
          <h3 className='plan-billing-hero-title'>
            {endsAt
              ? `Free month — unlimited use until ${formatPlanDate(endsAt)}`
              : 'Free month — unlimited use'}
          </h3>
          <p className='plan-billing-hero-text'>
            After that you&apos;ll have a monthly usage allowance
            {offerPrice ? `, or go unlimited for ${offerPrice}` : ''}.
          </p>
          {daysLeft && <span className='plan-billing-tag'>{daysLeft}</span>}
        </div>
      );
      footer = startCta(
        offerPrice
          ? `Start your subscription for ${offerPrice}`
          : 'Start your subscription',
        'Start subscription',
      );
      break;
    }
    case 'none': {
      body = (
        <Panel
          testId='plan-billing-usage'
          eyebrow='Free plan'
          headline={
            <span
              className={`plan-billing-usage-level level-${usage.level}`}
              data-testid='plan-billing-usage-level'
            >
              {USAGE_LEVEL_LABELS[usage.level]}
            </span>
          }
          caption="This month's usage"
          meta={
            usage.resetsAt
              ? `Resets ${formatPlanDate(usage.resetsAt)}`
              : undefined
          }
        />
      );
      footer = startCta(
        offerPrice ? `Unlimited use for ${offerPrice}` : 'Unlimited use',
        'Go unlimited',
      );
      break;
    }
    case 'personal_subscription': {
      const cancelling = subscription?.cancellingAt ?? null;
      body = (
        <Panel
          testId='plan-billing-summary'
          eyebrow='Dream, unlimited usage'
          headline={subscription ? formatPlanPrice(subscription) : 'Unlimited'}
          meta={
            cancelling
              ? `Won't renew after ${formatPlanDate(cancelling)}`
              : subscription?.renewsAt
                ? `Next payment ${formatPlanDate(subscription.renewsAt)}`
                : undefined
          }
        />
      );
      footer = (
        <div className='plan-billing-cta'>
          {cancelling ? (
            <button
              type='button'
              className='btn-plan-primary plan-billing-button'
              onClick={onRestoreSubscription}
              disabled={restoringSubscription}
              data-testid='plan-billing-restore'
            >
              {restoringSubscription ? 'Restoring…' : 'Restore'}
            </button>
          ) : (
            <button
              type='button'
              className='btn-plan-cancel'
              onClick={onCancelSubscription}
              data-testid='plan-billing-cancel'
            >
              Cancel subscription
            </button>
          )}
        </div>
      );
      break;
    }
    case 'plan_override': {
      body = (
        <Panel
          eyebrow='Unlimited — complimentary'
          headline='Nothing to pay'
          caption='Your Dream use is unlimited, on us.'
        />
      );
      break;
    }
    case 'organisation_seat': {
      const orgName = overview.organisationName ?? 'your organisation';
      body = (
        <>
          <Panel
            testId='plan-billing-organisation'
            eyebrow='Your subscription is managed by'
            headline={orgName}
            caption='To make changes, contact your organisation admin.'
          />
          {subscription?.cancellingAt && (
            <p
              className='plan-billing-text'
              data-testid='plan-billing-own-subscription'
            >
              Your own subscription ends on{' '}
              {formatPlanDate(subscription.cancellingAt)} and won&apos;t renew.
            </p>
          )}
        </>
      );
      break;
    }
  }

  return (
    <>
      <div className='plan-page-header'>
        <h1 className='plan-page-title'>Plan &amp; billing</h1>
      </div>
      <div
        className='plan-card plan-billing-card'
        data-testid='personal-plan-billing'
        data-source={source}
      >
        <h2 className='plan-card-title'>Your subscription</h2>
        {body}
        {error && <p className='topup-feedback error'>{error}</p>}
        {footer}
      </div>
    </>
  );
};

export default PersonalPlanBilling;
